/**
 * Copyright 2025 (c) Christian von Arnim, ISW University of Stuttgart (for umati and VDW e.V.)
 */
import React from 'react';
import { FilterBrowsename, FilterReferenceAndTypeId, FilterStartNode } from 'utilities/PubSub/NodeFilters';
import { NodeIdentifier, ns0 } from 'utilities/PubSub/PubSubAddressSpace';
import {
  useBrowseName,
  useNamespaceIndex,
  usePubSubNode,
  usePubSubNodes,
  usePubSubValue,
} from 'utilities/PubSub/PubSubHooks';

let debug = 1;

namespace nsIA {
  export const StackElementLightType = 1006;
}

function toStringLine(arg: undefined | string | string[] | { [k: string]: string | undefined | string[] }) {
  if (arg === undefined) {
    return 'undefined';
  } else if (Array.isArray(arg)) {
    if (arg.length === 0) {
      return '[]';
    }

    return arg.join(', ');
  } else if (typeof arg === 'object') {
    return toStringLine(Object.keys(arg).map((k) => `${k}: ${arg[k]}`));
  } else {
    return arg;
  }
}

interface StackElementProps {
  StackElementId: NodeIdentifier;
}

function StackElement({ StackElementId }: StackElementProps) {
  const ns = useNamespaceIndex({
    ia: 'http://opcfoundation.org/UA/IA/',
  });

  const childFilters = {
    NumberInList: FilterBrowsename({ Name: 'NumberInList' }),
    IsPartOfBase: FilterBrowsename({
      Name: 'IsPartOfBase',
      NamespaceIndex: ns.ia,
    }),
    SignalOn: FilterBrowsename({ Name: 'SignalOn', NamespaceIndex: ns.ia }),
    SignalMode: FilterBrowsename({ Name: 'SignalMode', NamespaceIndex: ns.ia }),
    SignalColor: FilterBrowsename({
      Name: 'SignalColor',
      NamespaceIndex: ns.ia,
    }),
  };

  const childNodes = usePubSubNode(childFilters, StackElementId);
  const childValues = usePubSubValue(childNodes);
  const browseName = useBrowseName(StackElementId);

  const browseNameTxt = browseName === undefined ? 'undefined' : browseName.Name;
  return (
    <div>
      <div>{browseNameTxt}</div>
      <div>{toStringLine(childValues)}</div>
    </div>
  );
}

export default function TestValue() {
  const ns = useNamespaceIndex({
    mt: 'http://opcfoundation.org/UA/MachineTool/',
    di: 'http://opcfoundation.org/UA/DI/',
    ia: 'http://opcfoundation.org/UA/IA/',
  });
  const filterManufacturer = [
    FilterBrowsename({ Name: 'Identification', NamespaceIndex: ns.di }),
    FilterBrowsename({ Name: 'Manufacturer', NamespaceIndex: ns.di }),
  ];
  const nodeManufacturer = usePubSubNode(filterManufacturer);
  const valueMaufacturer = usePubSubValue(nodeManufacturer);

  const nodeIdentification = usePubSubNode(FilterBrowsename({ Name: 'Identification', NamespaceIndex: ns.di }));
  // DO NOT check for the success here, all react hooks must always been called
  const identificationNodes = usePubSubNode(
    {
      SerialNumber: FilterBrowsename({
        Name: 'SerialNumber',
        NamespaceIndex: ns.di,
      }),
      ProductInstanceUri: FilterBrowsename({
        Name: 'ProductInstanceUri',
        NamespaceIndex: ns.di,
      }),
      Manufacturer: FilterBrowsename({
        Name: 'Manufacturer',
        NamespaceIndex: ns.di,
      }),
    },
    nodeIdentification,
  );

  const identificationValues = usePubSubValue(identificationNodes);

  const filterStacklight = [
    FilterBrowsename({ Name: 'Monitoring', NamespaceIndex: ns.mt }),
    FilterBrowsename({ Name: 'Stacklight', NamespaceIndex: ns.mt }),
  ];

  const nodeStacklight = usePubSubNode(filterStacklight);

  const stackElementNodes = usePubSubNodes(
    FilterReferenceAndTypeId({ Id: ns0.HasOrderedComponent }, { Id: nsIA.StackElementLightType, Namespace: ns.ia }),
    nodeStacklight,
  );

  /// Get the NumberInList property of each element by utelizing FilterStartNode() to set custom staringNode per filter
  /// create individual objects with filters per element
  const NumberInListFilterObjects = stackElementNodes.map((nId) => ({
    [nId]: [FilterStartNode(nId), FilterBrowsename({ Name: 'NumberInList' })],
  }));

  /// merge individual objets
  const NumberInListFilters = Object.assign({}, ...NumberInListFilterObjects);
  const numberInListNodes = usePubSubNode(NumberInListFilters);
  const numberInListValues = usePubSubValue(numberInListNodes);

  // Reverse sorting for demonstration (b and a swapped)
  const stackElementNodesSorted = stackElementNodes.sort((b, a) => numberInListValues[a] - numberInListValues[b]);
  const stackLight = stackElementNodesSorted.map((n) => <StackElement StackElementId={n} key={n} />);

  const activeProgramCurrentStateFilter = [
    FilterBrowsename({ Name: 'Production', NamespaceIndex: ns.mt }),
    FilterBrowsename({ Name: 'ActiveProgram', NamespaceIndex: ns.mt }),
    FilterBrowsename({ Name: 'State', NamespaceIndex: ns.mt }),
    FilterBrowsename({ Name: 'CurrentState' }), // Ns0
  ];

  const activeProgramCurrentState = usePubSubNode(activeProgramCurrentStateFilter);
  if (activeProgramCurrentState !== undefined) {
    console.debug('activeProgramCurrentState', activeProgramCurrentState);
  }
  const activeProgramCurrentStateNodes = usePubSubNode(
    {
      Number: FilterBrowsename({ Name: 'Number' }),
      Id: FilterBrowsename({ Name: 'Id' }),
    },
    activeProgramCurrentState,
  );
  const activeProgramCurrentStateValues = usePubSubValue(activeProgramCurrentStateNodes);

  return (
    <>
      <div>tetes</div>
      <pre>
        Debug: {++debug} <br />
        node: {nodeManufacturer} <br />
        NsIdx MT: {ns.mt} <br />
        Value: {valueMaufacturer !== undefined ? valueMaufacturer.toString() : 'undefined'} <br />
        NodeIdentification: {toStringLine(nodeIdentification)} <br />
        IdentificationNodes: {toStringLine(identificationNodes)} <br />
        IdentificationValues: {toStringLine(identificationValues)} <br />
        NodeStacklight: {toStringLine(nodeStacklight)} <br />
        StackElementNodes: {toStringLine(stackElementNodes)} <br />
        NumberInListValues: {toStringLine(numberInListValues)} <br />
        ActiveProgramCurrentState: {toStringLine(activeProgramCurrentState)} <br />
        ActiveProgramCurrentStateNodes: {toStringLine(activeProgramCurrentStateNodes)} <br />
        ActiveProgramCurrentStateValues: {toStringLine(activeProgramCurrentStateValues)} <br />
      </pre>
      {stackLight}
    </>
  );
}
